import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { aggregateMastery } from '@yuan-math/learning';
import { Badge, Card, MasteryBar, SectionHeading } from '@yuan-math/ui';
import { registry, useLearner } from '../state/LearnerContext.tsx';

/** The course catalog: available courses first, then what is being built. */
export function CoursesPage(): JSX.Element {
  const { profile } = useLearner();
  const now = Date.now();

  const available = registry.courses.filter((course) => course.status === 'available');
  const planned = registry.courses.filter((course) => course.status === 'planned');

  return (
    <div className="page page--wide">
      <header className="page-header">
        <h1 className="page__title">Courses</h1>
        <p className="page__lead">
          A course is a route through the curriculum, not a container for it. Every lesson in a course is also a
          concept on the map, and you can arrive at it from either direction.
        </p>
      </header>

      <section className="dashboard-section">
        <SectionHeading title="Available now" description="Written, tested and ready to study." />
        <div className="course-grid">
          {available.map((course) => {
            const concepts = registry.conceptsOfCourse(course.id);
            const lessons = registry.lessonsOfCourse(course.id);
            const mastery = profile ? aggregateMastery(concepts.map((c) => c.id), profile.concepts, now) : 0;
            return (
              <Card key={course.id} as="article" className="course-card">
                <div className="course-card__head">
                  <Badge tone="accent">{registry.level(course.level)?.title}</Badge>
                  <h2 className="course-card__title">
                    <Link to={`/courses/${course.id}`}>{course.title}</Link>
                  </h2>
                  <p className="course-card__subtitle">{course.subtitle}</p>
                </div>
                <p className="course-card__description">{course.description}</p>
                <p className="course-card__meta">
                  {registry.unitsOfCourse(course.id).length} units · {lessons.length} lessons · {concepts.length} concepts
                </p>
                {profile ? <MasteryBar value={mastery} label="Your mastery" size="sm" /> : null}
                <Link to={`/courses/${course.id}`} className="btn btn--secondary btn--sm">
                  Open course
                </Link>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="dashboard-section">
        <SectionHeading
          title="Being built"
          description="The architecture already carries these; the content is what is being written."
        />
        <ul className="planned-list">
          {planned.map((course) => (
            <li key={course.id} className="planned-item">
              <div>
                <p className="planned-item__title">{course.title}</p>
                <p className="planned-item__description">{course.description}</p>
              </div>
              <Badge>{registry.level(course.level)?.title}</Badge>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
